/** Main app scroll container (see `MainLayout` / `ScrollToTop`). */
export function getLayoutBodyScrollRoot(): HTMLElement | null {
  const el = document.querySelector('.layout-body');
  return el instanceof HTMLElement ? el : null;
}

/**
 * Spy line offset — matches `.tasklist-json-anchor { scroll-margin-top }` (24px desktop;
 * Replit `scroll-mt-24` is 96px in their Tailwind scale; we use literal 24px in SCSS).
 */
export const TOC_SCROLL_SPY_OFFSET_PX = 24;

/** Reads `scroll-margin-top` from a section anchor when available. */
export function getTocScrollSpyThresholdPx(anchor?: Element | null): number {
  if (anchor instanceof HTMLElement) {
    const marginTop = parseFloat(getComputedStyle(anchor).scrollMarginTop);
    if (!Number.isNaN(marginTop) && marginTop > 0) {
      return Math.ceil(marginTop);
    }
  }
  return TOC_SCROLL_SPY_OFFSET_PX;
}

export function getTocScrollSpyThresholdForItems(items: { id: string }[]): number {
  for (const item of items) {
    const el = document.getElementById(item.id);
    if (el) return getTocScrollSpyThresholdPx(el);
  }
  return TOC_SCROLL_SPY_OFFSET_PX;
}

/** Last TOC target whose heading is nearest the spy line in the upper viewport. */
export function resolveTocActiveId(
  items: { id: string }[],
  thresholdPx: number,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800
): string | null {
  if (!items.length) return null;

  const maxHeadingTop = viewportHeight * 0.5;
  let bestId = items[0].id;
  let bestDistance = Infinity;
  let foundInUpperViewport = false;

  for (const item of items) {
    const el = document.getElementById(item.id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top;
    if (top > maxHeadingTop) continue;
    const distance = Math.abs(top - thresholdPx);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = item.id;
      foundInUpperViewport = true;
    }
  }

  if (foundInUpperViewport) return bestId;

  /** Fallback at page bottom: last section whose top has passed the spy line. */
  let active = items[0].id;
  for (const item of items) {
    const el = document.getElementById(item.id);
    if (!el) continue;
    if (el.getBoundingClientRect().top - thresholdPx <= 1) {
      active = item.id;
    } else {
      break;
    }
  }
  return active;
}

export type TocScrollSpySyncResult = {
  activeId: string | null;
  releaseLock: boolean;
};

/**
 * Resolves scroll-driven TOC highlight. When `lockedId` is set (TOC click / smooth scroll),
 * keeps that id until the target reaches the spy line or the lock is cleared after scroll settles.
 */
export function syncTocScrollSpyState(
  items: { id: string }[],
  lockedId: string | null
): TocScrollSpySyncResult {
  if (!items.length) return { activeId: null, releaseLock: false };

  const threshold = getTocScrollSpyThresholdForItems(items);
  const resolved = resolveTocActiveId(items, threshold);
  if (!resolved) return { activeId: null, releaseLock: false };

  if (!lockedId) return { activeId: resolved, releaseLock: false };
  if (resolved === lockedId) return { activeId: resolved, releaseLock: true };

  const target = document.getElementById(lockedId);
  if (target && target.getBoundingClientRect().top - threshold <= 1) {
    return { activeId: lockedId, releaseLock: true };
  }

  return { activeId: lockedId, releaseLock: false };
}

/** Scroll the main layout container to a section anchor (hash links use CMS ids such as `general-expectations`). */
export function scrollLayoutBodyToElementId(
  id: string,
  behavior: ScrollBehavior = 'smooth'
): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior, block: 'start' });
  return true;
}

/**
 * Retries hash scrolling until the target mounts (async tasklist JSON) or attempts are exhausted.
 * Returns a cleanup that cancels pending retries.
 */
export function scrollLayoutBodyToHash(
  hash: string,
  options?: { behavior?: ScrollBehavior; maxAttempts?: number }
): () => void {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!id) return () => {};

  const behavior = options?.behavior ?? 'smooth';
  const maxAttempts = options?.maxAttempts ?? 6;
  let cancelled = false;
  let attempt = 0;
  const timers: ReturnType<typeof setTimeout>[] = [];

  const tryScroll = () => {
    if (cancelled) return;
    if (scrollLayoutBodyToElementId(id, behavior)) return;
    attempt += 1;
    if (attempt <= maxAttempts) {
      timers.push(setTimeout(tryScroll, attempt * 100));
    }
  };

  tryScroll();
  requestAnimationFrame(tryScroll);

  return () => {
    cancelled = true;
    timers.forEach(clearTimeout);
  };
}

/** Attach scroll/resize listeners on `.layout-body` (not `window` — see MainLayout). */
export function subscribeLayoutBodyScroll(onScroll: () => void): () => void {
  const scrollRoot = getLayoutBodyScrollRoot();
  scrollRoot?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  return () => {
    scrollRoot?.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}
